import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipCounts,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import type { CurrentTableViewOptions } from "_shared/_views/list-pages/use-current-table-view";
import type { DocumentFilterParametersProps } from "documents/_api/commands/_schema/document-filter-schema";
import type { DocumentListCounts } from "documents/_api/queries/_schemas/document-list-counts-schema";
import { DocumentCategory, DocumentOwner } from "documents/categories/document-category";

/**
 * The tabs and chips of the Documents page, and how its URL spells them (#6720).
 *
 * The page was Contracts, with Templates, Conflicts, Contracts & Policies and Archived tabs. A tab is
 * a different kind of thing, so the categories and the archive became chips. Templates keep a tab of
 * their own, the one stated exception (#6557): a template is a document, but it is copied into
 * proposals rather than read. The chips are three groups, which narrow each other: the categories
 * (one or more widen the list), whose documents (Mine or Others'), and Archived, last.
 */

/** The path of the page, which is the Documents tab. Templates is a segment under it. */
export const DOCUMENTS_PATH = "/documents";

export enum DocumentsTab {
  Documents = "documents",
  Templates = "templates",
}

export const CATEGORY_PARAMETER = "category";
export const OWNER_PARAMETER = "owner";
export const STAGE_PARAMETER = "stage";
export const ARCHIVED_STAGE = "archived";

/** Each category chip's value in the URL, and the category `GET documents` matches exactly for it (#6710). */
const categoryByChip: Readonly<Record<string, DocumentCategory>> = {
  policies: DocumentCategory.Policies,
  conflicts: DocumentCategory.Conflicts,
  proposals: DocumentCategory.Proposals,
  work: DocumentCategory.Work,
  chat: DocumentCategory.Chat,
  "knowledge-base": DocumentCategory.KnowledgeBase,
};

/**
 * Each owner chip's value in the URL, and the owner `GET documents` takes for it. Mine is the reader's
 * organization's, not only their own uploads: My Policies, which it replaces, left out a coworker's
 * policy and listed it as a vendor's until #7069.
 */
const ownerByChip: Readonly<Record<string, DocumentOwner>> = {
  mine: DocumentOwner.MyOrganization,
  others: DocumentOwner.OtherOrganizations,
};

const categoryChips = [
  { value: "policies", label: "Policies" },
  { value: "conflicts", label: "Conflicts" },
  { value: "proposals", label: "Proposals" },
  { value: "work", label: "Work" },
  { value: "chat", label: "Chat" },
  { value: "knowledge-base", label: "Knowledge base" },
];

/** Only policies and conflicts are ever templates. */
const TEMPLATE_CATEGORY_CHIPS: readonly string[] = ["policies", "conflicts"];

const ownerGroup: ListChipGroup = {
  parameter: OWNER_PARAMETER,
  label: "Owner",
  chips: [
    { value: "mine", label: "Mine" },
    { value: "others", label: "Others'" },
  ],
  // The API takes one owner, so the group is a segmented control (#6720). A link that names both
  // means everybody's, which is All.
  isSingle: true,
  sameAsNone: ["mine", "others"],
};

const stageGroup: ListChipGroup = {
  parameter: STAGE_PARAMETER,
  label: "Stage",
  chips: [{ value: ARCHIVED_STAGE, label: "Archived" }],
};

const chipGroupsByTab: Readonly<Record<DocumentsTab, readonly ListChipGroup[]>> = {
  [DocumentsTab.Documents]: [
    { parameter: CATEGORY_PARAMETER, label: "Category", chips: categoryChips },
    ownerGroup,
    stageGroup,
  ],
  [DocumentsTab.Templates]: [
    {
      parameter: CATEGORY_PARAMETER,
      label: "Category",
      chips: categoryChips.filter((chip) => TEMPLATE_CATEGORY_CHIPS.includes(chip.value)),
    },
    ownerGroup,
    stageGroup,
  ],
};

/** The chip groups of `tab`, in the order they are drawn. Archived is last. */
export function documentChipGroups(tab: DocumentsTab): readonly ListChipGroup[] {
  return chipGroupsByTab[tab];
}

/** The tab a path segment names: Templates for `templates`, the Documents tab for none. Undefined for any other. */
export function readDocumentsTab(segment: string | undefined): DocumentsTab | undefined {
  if (segment === undefined) return DocumentsTab.Documents;
  return segment.toLowerCase() === DocumentsTab.Templates ? DocumentsTab.Templates : undefined;
}

/** The path of `tab`: the page's own for Documents, a segment under it for Templates. */
export function documentsTabPath(tab: DocumentsTab): string {
  return tab === DocumentsTab.Templates ? `${DOCUMENTS_PATH}/${DocumentsTab.Templates}` : DOCUMENTS_PATH;
}

/** The chips `searchParams` names on `tab`. A category the tab does not have is dropped. */
export function readDocumentChips(tab: DocumentsTab, searchParams: URLSearchParams): ListChipSelection {
  return readListChips(documentChipGroups(tab), searchParams);
}

/** Whether `selection` lists the archive rather than the open documents. */
export function listsArchive(selection: ListChipSelection): boolean {
  return (selection[STAGE_PARAMETER] ?? []).includes(ARCHIVED_STAGE);
}

/** The categories `selection` chose. None is every category. */
export function chosenCategories(selection: ListChipSelection): DocumentCategory[] {
  return (selection[CATEGORY_PARAMETER] ?? []).map((value) => categoryByChip[value]);
}

/**
 * Whether the rows can be of more than one category, so each needs its Type (#5780): every category
 * but one chip. On Templates no chip is policies and conflicts together.
 */
export function rowsMixCategories(selection: ListChipSelection): boolean {
  return (selection[CATEGORY_PARAMETER] ?? []).length !== 1;
}

/**
 * What `GET documents` is asked for the tab and its chips: exact categories, the template status, the
 * owner and the archived state (#6710). The archive replaces the open list; it does not add to it.
 */
export function documentListFilter(
  tab: DocumentsTab,
  selection: ListChipSelection,
): Pick<DocumentFilterParametersProps, "categories" | "isTemplate" | "owner" | "isArchived"> {
  const categories = chosenCategories(selection);
  const [owner] = selection[OWNER_PARAMETER] ?? [];
  return {
    categories: categories.length ? categories : undefined,
    isTemplate: tab === DocumentsTab.Templates,
    owner: owner ? ownerByChip[owner] : undefined,
    isArchived: listsArchive(selection),
  };
}

/** Each chip's count, by the value it writes to the URL, from the list's one counts answer. */
export function documentChipCounts(counts: DocumentListCounts | undefined): ListChipCounts | undefined {
  if (!counts) return undefined;
  const byCategory: Record<string, number | undefined> = {};
  for (const [chip, category] of Object.entries(categoryByChip)) byCategory[chip] = counts.categories[category];
  const byOwner: Record<string, number | undefined> = {};
  for (const [chip, owner] of Object.entries(ownerByChip)) byOwner[chip] = counts.owners[owner];
  return {
    [CATEGORY_PARAMETER]: byCategory,
    [OWNER_PARAMETER]: byOwner,
    [STAGE_PARAMETER]: { [ARCHIVED_STAGE]: counts.archived },
  };
}

/**
 * What the list holds, for its search box and its empty state: "documents", "archived templates",
 * "policies", "your conflicts and work documents". Named from the chips, so an empty list says which
 * list is empty rather than which tab.
 */
export function documentListSubject(tab: DocumentsTab, selection: ListChipSelection): string {
  const chosen = (selection[CATEGORY_PARAMETER] ?? []).map(
    (value) => categoryChips.find((chip) => chip.value === value)!.label.toLowerCase(),
  );
  const noun = tab === DocumentsTab.Templates ? "templates" : "documents";
  // "policies and conflicts" are already documents; "work" and "chat" need the noun after them.
  const kinds = chosen.length === 0
    ? noun
    : tab === DocumentsTab.Templates
      ? `${joinWithAnd(chosen.map((kind) => kind.replace(/ies$/, "y")))} templates`
      : chosen.every((kind) => kind === "policies" || kind === "conflicts")
        ? joinWithAnd(chosen)
        : `${joinWithAnd(chosen)} documents`;
  const [owner] = selection[OWNER_PARAMETER] ?? [];
  const whose = owner === "mine" ? "your " : owner === "others" ? "others' " : "";
  return `${listsArchive(selection) ? "archived " : ""}${whose}${kinds}`;
}

function joinWithAnd(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

/** The parameters a stored view keeps besides its chips: the name filter, the sort and the page size. */
const KEPT_PARAMETERS: readonly string[] = ["name", "orderBy", "orderDir", "pageSize"];

/**
 * A stored view as this page still reads it: on a tab the page has, with a chip it no longer has
 * dropped. A view stored while the page was Contracts is not carried over: its table key was
 * `contracts`, and the reader's saved view resets once (#6720).
 */
export function keepDocumentsView(view: ListViewPath): ListViewPath | undefined {
  const segment = view.tabPath === DOCUMENTS_PATH
    ? undefined
    : view.tabPath.startsWith(`${DOCUMENTS_PATH}/`)
      ? view.tabPath.slice(DOCUMENTS_PATH.length + 1)
      : null;
  if (segment === null) return undefined;
  const tab = readDocumentsTab(segment);
  if (tab === undefined) return undefined;

  const stored = new URLSearchParams(view.search);
  const kept = writeListChips(documentChipGroups(tab), readDocumentChips(tab, stored), new URLSearchParams());
  for (const parameter of KEPT_PARAMETERS) {
    const value = stored.get(parameter);
    if (value !== null) kept.set(parameter, value);
  }
  return { tabPath: documentsTabPath(tab), search: kept.toString() };
}

/**
 * The page's Default view, declared once: the Documents tab with no chip. Its bare path is the view,
 * so a plain visit opens the reader's starred or last view, else this one.
 */
export const DOCUMENTS_CURRENT_VIEW: CurrentTableViewOptions = {
  tableKey: "documents",
  basePath: DOCUMENTS_PATH,
  keepWhatStillExists: keepDocumentsView,
};

/**
 * Where a Contracts URL opens now (#6720). Bookmarks, emails and the assistant's answers name them,
 * so each lands on the rows it listed:
 *
 * - `/contracts/templates` → `/documents/templates`, its `context=conflicts|policy` chip as a category;
 * - `/contracts/conflicts` → `/documents?category=conflicts`;
 * - `/contracts/policies` → `/documents?category=policies`, `context=mine` as `owner=mine` and
 *   `context=clientVendor` as `owner=others`;
 * - `/contracts/archived` → `/documents?stage=archived`;
 * - `/contracts` → `/documents`, which opens the reader's kept view.
 *
 * The name filter, the sort and the page size go with it. Undefined for a segment Contracts never had.
 */
export function retiredContractsPath(segment: string | undefined, searchParams: URLSearchParams): string | undefined {
  const named = segment?.toLowerCase();
  const context = searchParams.get("context");
  const next = new URLSearchParams();
  let tab = DocumentsTab.Documents;
  switch (named) {
    case undefined:
      break;
    case "templates":
      tab = DocumentsTab.Templates;
      if (context === "conflicts") next.set(CATEGORY_PARAMETER, "conflicts");
      if (context === "policy") next.set(CATEGORY_PARAMETER, "policies");
      break;
    case "conflicts":
      next.set(CATEGORY_PARAMETER, "conflicts");
      break;
    case "policies":
      next.set(CATEGORY_PARAMETER, "policies");
      if (context === "mine") next.set(OWNER_PARAMETER, "mine");
      if (context === "clientVendor") next.set(OWNER_PARAMETER, "others");
      break;
    case "archived":
      next.set(STAGE_PARAMETER, ARCHIVED_STAGE);
      break;
    default:
      return undefined;
  }
  for (const parameter of KEPT_PARAMETERS) {
    const value = searchParams.get(parameter);
    if (value !== null) next.set(parameter, value);
  }
  const query = next.toString();
  return query ? `${documentsTabPath(tab)}?${query}` : documentsTabPath(tab);
}
